import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { Input } from "@/components/ui/input";

export function SessionsEditor({ value, onChange }) {
  const setAt = (i, k, v) => onChange(value.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  return (
    <div data-testid="f-sessions">
      <div className="flex items-center justify-between mb-2">
        <span className="jt-eyebrow">Sessions (departure times guests can book)</span>
        <button type="button" onClick={() => onChange([...value, { label: "Morning", time: "06:30" }])} className="text-sm text-[#C86D51]" data-testid="f-add-session">+ Add session</button>
      </div>
      <div className="space-y-2">
        {value.map((s, i) => (
          <div key={i} className="flex gap-2 items-center" data-testid={`f-session-${i}`}>
            <Input className="flex-1" placeholder="Label (e.g. Morning)" value={s.label} onChange={(e) => setAt(i, "label", e.target.value)} data-testid={`f-session-label-${i}`}/>
            <Input className="w-32" type="time" value={s.time} onChange={(e) => setAt(i, "time", e.target.value)} data-testid={`f-session-time-${i}`}/>
            <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="p-2 text-[#C86D51]" aria-label="Remove session"><X size={16}/></button>
          </div>
        ))}
        {value.length === 0 && <p className="text-xs text-[#C86D51]">No sessions: guests will see every slot you add for this safari.</p>}
      </div>
    </div>
  );
}

const STATUSES = ["new", "contacted", "confirmed", "declined"];
const TONE = { new: "bg-[#FBF1EC] text-[#C86D51]", contacted: "bg-[#F5F0EB] text-[#5C6B63]", confirmed: "bg-[#1E3A2B] text-white", declined: "bg-[#F6E3DE] text-[#A33A2A]" };

export function RequestList() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const load = () => api.get("/admin/requests", { params: filter ? { status: filter } : {} }).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = async (r, status) => {
    try { await api.patch(`/admin/requests/${r.id}`, { status, admin_note: r.admin_note || "" }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="mt-6">
      <div className="flex gap-2 mb-4 flex-wrap">
        {["", ...STATUSES].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`rq-filter-${f || "all"}`}
            className={`px-3 py-1.5 rounded-full text-sm border ${filter === f ? "bg-[#1E3A2B] text-white border-[#1E3A2B]" : "bg-white border-[#E2DACF]"}`}>{f || "all"}</button>
        ))}
      </div>
      <div className="bg-white rounded-xl ring-1 ring-black/5 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F0EB] text-left"><tr>
            <th className="p-3">Ref</th><th>Safari</th><th>Date / session</th><th>Party</th><th>Guest</th><th>Notes</th><th>Status</th><th className="pr-3">Update</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t align-top" data-testid={`rq-row-${r.request_ref}`}>
                <td className="p-3 font-mono text-[#1E3A2B]">{r.request_ref}</td>
                <td className="py-3">{r.safari_name}</td>
                <td className="py-3">{r.date}<div className="text-xs text-[#5C6B63]">{r.session || "Any session"}</div></td>
                <td className="py-3">{r.booking_type === "private" ? `Private × ${r.vehicles}` : "Shared"}<div className="text-xs text-[#5C6B63]">{r.adults}A {r.children}C</div></td>
                <td className="py-3">{r.guest_name}<div className="text-xs"><a className="text-[#1E3A2B] underline" href={`https://wa.me/${r.guest_mobile.replace(/\D/g, "").replace(/^(\d{10})$/, "91$1")}`} target="_blank" rel="noopener noreferrer">{r.guest_mobile}</a></div><div className="text-xs text-[#5C6B63]">{r.guest_email}</div></td>
                <td className="py-3 text-xs text-[#5C6B63] max-w-[220px]">{r.notes || "—"}</td>
                <td className="py-3"><span className={`px-2 py-0.5 rounded-full text-xs ${TONE[r.status]}`} data-testid={`rq-status-${r.request_ref}`}>{r.status}</span></td>
                <td className="py-3 pr-3">
                  <div className="flex flex-col gap-1 items-start">
                    {STATUSES.filter((s) => s !== r.status && s !== "new").map((s) => (
                      <button key={s} onClick={() => update(r, s)} className="text-xs underline text-[#1E3A2B]" data-testid={`rq-${s}-${r.request_ref}`}>Mark {s}</button>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-[#5C6B63]">No requests</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-[#5C6B63] mt-3">Tip: to confirm a request, add a slot for that date in the Slots tab and send the guest the booking link, or book it for them.</p>
    </div>
  );
}
